import React, {createContext, useContext, useEffect, useState} from "react";
import {subscriptionServices} from "../../service/subscriptionService";
import {
    Alert,
    Box, Button, Checkbox,
    DialogActions,
    DialogContent,
    Divider,
    TextField,
    ToggleButton,
    ToggleButtonGroup
} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import Spinner from "../spinner/Spinner";
import {useStripe, useElements, CardElement} from "@stripe/react-stripe-js";
import {AuthBackButton, AuthCard, AuthCardField, AuthLayout, AuthPaymentNote, AuthSetupSteps} from "./AuthDesign";
import {getSettingsColors} from "../settings/SettingsDesign";
import {businessSubscriptionPlans, countries, individualSubscriptionPlans} from "../utils/DataList";
import Cookies from "universal-cookie/es6";
import {useForm} from "react-hook-form";
import CustomSnackbar from "../alert/CustomSnackbar";
import {BillingDetailsCountrySelect} from "../settings/subscription/UpdateBillingDetails";
import {useSelector} from "react-redux";

import PlanCard from "../settings/subscription/PlanCard";

export const AccountSetupContext = createContext(null);

export default function AccountSetup(props) {
    const [step, setStep] = useState(1);
    const [planName, setPlanName] = useState("");
    const [subscriptionStatus, setSubscriptionStatus] = useState("");
    const [latestInvoicePaymentIntentStatus, setLatestInvoicePaymentIntentStatus] = useState("");
    const [billingDetails, setBillingDetails] = useState(null);
    const [vatRate, setVatRate] = useState(0);
    const [vatDisplayName, setVatDisplayName] = useState("VAT");
    const [updateOnlyPaymentMethod, setUpdateOnlyPaymentMethod] = useState(false);

    return (
        <AccountSetupContext.Provider
            value={{
                step, setStep,
                planName, setPlanName,
                subscriptionStatus, setSubscriptionStatus,
                latestInvoicePaymentIntentStatus, setLatestInvoicePaymentIntentStatus,
                billingDetails, setBillingDetails,
                vatRate, setVatRate,
                vatDisplayName, setVatDisplayName,
                updateOnlyPaymentMethod, setUpdateOnlyPaymentMethod
            }}>
            <AuthLayout session wide compact>
                <AuthSetupSteps step={step}/>
                {step === 1 && <Step1ChoosePlan {...props}/>}
                {step === 2 && <Step2AddBillingDetails {...props}/>}
                {step === 3 && <Step3ConfirmPayment {...props}/>}
            </AuthLayout>
        </AccountSetupContext.Provider>
    );
}

const Step1ChoosePlan = () => {
    const {setStep, setPlanName, setUpdateOnlyPaymentMethod} = useContext(AccountSetupContext);
    const [planType, setPlanType] = useState("individual");

    const currentSubscriptionData = useSelector(state => state?.auth?.subscription);

    const handleChoosePlan = (planName) => {
        individualSubscriptionPlans.forEach((plan) => {
            if (planName === plan?.name) {
                setPlanName(plan?.name);
                setUpdateOnlyPaymentMethod(false); // reset, because we need to create a new subscription.
                setStep(2);
            }
        });
        businessSubscriptionPlans.forEach((plan) => {
            if (planName === plan?.name) {
                setPlanName(plan?.name);
                setUpdateOnlyPaymentMethod(false); // reset, because we need to create a new subscription.
                setStep(2);
            }
        });
    };

    const handleToggleButtonChange = (event, newPlanType) => {
        if (newPlanType === null) {
            return false;
        }
        setPlanType(newPlanType);
    };

    return (
        <AuthCard maxWidth={1180} title="Choose your plan" description="Choose the capacity and features that fit your campaigns. You can change your plan later.">

            <DialogContent sx={{paddingLeft: 1, paddingRight: 1, paddingTop: 1}}>
                <Box textAlign="center" pb={2}>
                    <ToggleButtonGroup
                        aria-label="Plan category"
                        sx={{bgcolor: theme => getSettingsColors(theme).inset, p: 0.5, gap: 0.5, borderRadius: '10px',
                            '& .MuiToggleButtonGroup-grouped': {border: 0, borderRadius: '7px', minHeight: 35, px: 2, py: 0.75, fontSize: 13, textTransform: 'none'}}}
                        color="primary"
                        value={planType}
                        exclusive
                        onChange={handleToggleButtonChange}
                    >
                        <ToggleButton value="individual">Individual</ToggleButton>
                        <ToggleButton value="business">Business</ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                {planType === "individual" &&
                    <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: 'repeat(3, minmax(0, 1fr))'}, gap: 2}}>
                        {individualSubscriptionPlans?.map((plan, i) => {
                            if (currentSubscriptionData?.status === "canceled" && plan?.name === "Free") {
                                return false;
                            }

                            return (
                                <Box sx={{minWidth: 0}} key={i}>
                                    <PlanCard
                                        planName={plan?.name}
                                        price={plan?.price}
                                        events={plan?.events}
                                        overage={plan?.overagePrice}
                                        customDomains={plan?.customDomains}
                                        months={plan?.dataRetentionInMonths}
                                        totalNumberOfRecords={plan?.totalNumberOfRecords}
                                        users={plan?.users}
                                        choosePlan={handleChoosePlan}
                                        integrations={plan?.integrations}
                                    />
                                </Box>
                            );
                        })}
                    </Box>
                }

                {planType === "business" &&
                    <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: 'repeat(2, minmax(0, 1fr))'}, gap: 2}}>
                        {businessSubscriptionPlans?.map((plan, i) => {
                            return (
                                <Box sx={{minWidth: 0}} key={i}>
                                    <PlanCard
                                        planName={plan?.name}
                                        price={plan?.price}
                                        events={plan?.events}
                                        overage={plan?.overagePrice}
                                        customDomains={plan?.customDomains}
                                        months={plan?.dataRetentionInMonths}
                                        totalNumberOfRecords={plan?.totalNumberOfRecords}
                                        users={plan?.users}
                                        choosePlan={handleChoosePlan}
                                        integrations={plan?.integrations}
                                    />
                                </Box>
                            );
                        })}
                    </Box>
                }
                <Box textAlign="center" pt={1} sx={{fontSize: 12, color: "text.disabledLight"}}>
                    * An event should be defined as every visit / click / conversion.
                </Box>
                <Box textAlign="center" pt={1} sx={{fontSize: 12, color: "text.disabledLight"}}>
                    * Overage charge occurs when the plan’s included events limit is exceeded.
                </Box>
                <Box textAlign="center" pt={1} sx={{fontSize: 12, color: "text.disabledLight"}}>
                    * All prices exclude VAT. Applicable VAT may be added at checkout based on your billing country.
                </Box>
            </DialogContent>
        </AuthCard>
    );
};


const cookies = new Cookies();

function Step2AddBillingDetails() {
    const {step, setStep, setBillingDetails, billingDetails, setVatRate, setVatDisplayName} = useContext(AccountSetupContext);

    const {register, handleSubmit, control, setValue, formState: {errors}} = useForm();

    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");

    const handleOnSubmit = async (data) => {
        setLoading(true);
        setErrorMsg("");

        subscriptionServices.verifyVat({country: data?.country?.code, vatNumber: data?.vatNumber}).then((result) => {
            setBillingDetails({
                name: data?.name,
                address1: data?.address1,
                address2: data?.address2,
                country: data?.country?.code,
                city: data?.city,
                state: data?.state,
                zip: data?.zip,
                vatNumber: data?.vatNumber,
            });
            setVatRate(result?.vatRate);
            setVatDisplayName(result?.displayName);
            setStep(3);
        }).catch((error) => {
            setErrorMsg(error?.response?.data?.error);
        }).finally(() => {
            setLoading(false);
        });
    };

    const handlePreviousStep = () => {
        setStep(1);
    };

    useEffect(() => {
        if (step === 2) {
            setLoading(true);
            setValue("name", billingDetails?.name);
            setValue("address1", billingDetails?.address1);
            setValue("address2", billingDetails?.address2);
            setValue("city", billingDetails?.city);
            setValue("state", billingDetails?.state);
            setValue("zip", billingDetails?.zip);
            setValue("vatNumber", billingDetails?.vatNumber);
            countries.forEach((item) => {
                if (item.code === billingDetails?.country && item.code !== "") {
                    setValue("country", {code: item.code, label: item.label});
                }
            });
            setLoading(false);
        }
    }, [step, setValue, billingDetails]);

    return (
        <AuthCard title="Billing details" description="Add your billing information so we can prepare your subscription and invoices.">
            {loading && <Spinner size={32} overComponentBox={true}/>}
            <DialogContent sx={{padding: "6px"}}>
                <form onSubmit={handleSubmit(handleOnSubmit)} autoComplete={"off"}>
                    {errorMsg !== "" &&
                        <CustomSnackbar message={errorMsg} timeout={2500} severity={"error"}/>
                    }
                    <Box className="auth-billing-fields">
                        <Box p={1} flexGrow={1}>
                            <TextField
                                error={!!errors?.name}
                                variant="outlined"
                                margin="dense"
                                size="small"
                                id="name"
                                {...register("name", {required: true, maxLength: 100})}
                                name="name"
                                label="Name / Company Name *"
                                placeholder="Enter your name or company name"
                                type="text"
                                fullWidth
                                slotProps={{
                                    inputLabel: {
                                        shrink: true
                                    }
                                }}
                            />
                        </Box>
                        <Box p={1} flexGrow={1}>
                            <BillingDetailsCountrySelect control={control} register={register} errors={errors}/>
                        </Box>
                        <Box p={1} flexGrow={1}>
                            <TextField
                                error={!!errors?.vatNumber}
                                variant="outlined"
                                margin="dense"
                                size="small"
                                id="vatNumber"
                                {...register("vatNumber", {maxLength: 100})}
                                name="vatNumber"
                                label="VAT Number (For EU Business customers)"
                                placeholder="Enter your VAT number"
                                type="text"
                                fullWidth
                                slotProps={{
                                    inputLabel: {
                                        shrink: true
                                    }
                                }}
                            />
                        </Box>
                        <Box p={1} flexGrow={1}>
                            <TextField
                                error={!!errors?.address1}
                                variant="outlined"
                                margin="dense"
                                size="small"
                                id="address1"
                                {...register("address1", {maxLength: 100})}
                                name="address1"
                                label="Address 1"
                                placeholder="Enter your address 1"
                                type="text"
                                fullWidth
                                slotProps={{
                                    inputLabel: {
                                        shrink: true
                                    }
                                }}
                            />
                        </Box>
                        <Box p={1} flexGrow={1}>
                            <TextField
                                error={!!errors?.address2}
                                variant="outlined"
                                margin="dense"
                                size="small"
                                id="address2"
                                {...register("address2", {maxLength: 100})}
                                name="address2"
                                label="Address 2"
                                placeholder="Enter your address 2"
                                type="text"
                                fullWidth
                                slotProps={{
                                    inputLabel: {
                                        shrink: true
                                    }
                                }}
                            />
                        </Box>
                        <Box p={1} flexGrow={1}>
                            <TextField
                                error={!!errors?.city}
                                variant="outlined"
                                margin="dense"
                                size="small"
                                id="city"
                                {...register("city", {maxLength: 100})}
                                name="city"
                                label="City"
                                placeholder="Enter your city"
                                type="text"
                                fullWidth
                                slotProps={{
                                    inputLabel: {
                                        shrink: true
                                    }
                                }}
                            />
                        </Box>
                        <Box p={1} flexGrow={1}>
                            <TextField
                                error={!!errors?.state}
                                variant="outlined"
                                margin="dense"
                                size="small"
                                id="state"
                                {...register("state", {maxLength: 100})}
                                name="state"
                                label="State"
                                placeholder="Enter your state"
                                type="text"
                                fullWidth
                                slotProps={{
                                    inputLabel: {
                                        shrink: true
                                    }
                                }}
                            />
                        </Box>
                        <Box p={1} flexGrow={1}>
                            <TextField
                                error={!!errors?.zip}
                                variant="outlined"
                                margin="dense"
                                size="small"
                                id="zip"
                                {...register("zip", {maxLength: 100})}
                                name="zip"
                                label="Zip"
                                placeholder="Enter your zip code"
                                type="text"
                                fullWidth
                                slotProps={{
                                    inputLabel: {
                                        shrink: true
                                    }
                                }}
                            />
                        </Box>
                    </Box>
                </form>
            </DialogContent>
            <DialogActions sx={{padding: 2}}>
                <AuthBackButton onClick={handlePreviousStep} disabled={loading}/>
                <Button type="submit" color="primary" variant="contained" onClick={handleSubmit(handleOnSubmit)}>
                    Next
                </Button>
            </DialogActions>
        </AuthCard>
    );
}

const Step3ConfirmPayment = () => {
    const theme = useTheme();
    const {step, setStep, planName, billingDetails, vatRate, vatDisplayName, updateOnlyPaymentMethod, setUpdateOnlyPaymentMethod} = useContext(AccountSetupContext);

    const stripe = useStripe();
    const elements = useElements();

    const [loading, setLoading] = useState(false);
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [acceptTermsError, setAcceptTermsError] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [couponResult, setCouponResult] = useState(null);
    const [coupon, setCoupon] = useState("");
    const [referralCode, setReferralCode] = useState("");
    const [planPrice, setPlanPrice] = useState(0.00);
    const [totalPrice, setTotalPrice] = useState(0.00);
    const [vatAmount, setVatAmount] = useState(0.00);
    const [discount, setDiscount] = useState(0.00);

    const handleConfirmPayment = async event => {
        event.preventDefault();

        if (!acceptTerms) {
            setAcceptTermsError(true);
            return;
        }

        setLoading(true);
        setErrorMsg("");

        if (!stripe || !elements) {
            return;
        }

        const {error, paymentMethod} = await stripe.createPaymentMethod({
            type: "card",
            card: elements.getElement(CardElement)
        });

        if (error) {
            setLoading(false);
            setErrorMsg(error?.message);
            return;
        }

        const createNewSubscriptionStatuses = ["", "incomplete", "incomplete_expired", "canceled", "unpaid"];

        const existingSubscription = await subscriptionServices.fetchSubscription(false, false, false)

        // Create new subscription.
        if (!updateOnlyPaymentMethod && (existingSubscription?.plan?.stripeSubscriptionId === "" || createNewSubscriptionStatuses.includes(existingSubscription?.plan?.status))) {
            await subscriptionServices.create({
                planName,
                paymentMethodId: paymentMethod?.id,
                coupon: couponResult?.coupon,
                referralCode,
                name: billingDetails?.name,
                address1: billingDetails?.address1,
                address2: billingDetails?.address2,
                country: billingDetails?.country,
                city: billingDetails?.city,
                state: billingDetails?.state,
                zip: billingDetails?.zip,
                vatNumber: billingDetails?.vatNumber
            })
                .then(async (result) => {
                    if (result?.status === "active") {
                        let skroClickID = cookies.get("sclid");
                        if (skroClickID !== null && skroClickID !== "" && skroClickID !== undefined) {
                            await subscriptionServices.skroPostback({
                                clickId: skroClickID,
                                payout: totalPrice
                            }).finally(() => {
                                document.location.replace("/");
                            });
                        } else {
                            document.location.replace("/");
                        }
                    } else if (result?.paymentIntentStatus === "requires_action") {
                        await stripe.confirmCardPayment(result?.paymentIntentClientSecret, {payment_method: result?.paymentMethodId})
                            .then(async (result) => {
                                if (result?.error) {
                                    setErrorMsg(result?.error?.message);
                                    setUpdateOnlyPaymentMethod(true);
                                } else {
                                    if (result.paymentIntent.status === 'succeeded') {
                                        // There's a risk of the customer closing the window before callback
                                        // execution. To handle this case, set up a webhook endpoint and
                                        // listen to invoice.paid. This webhook endpoint returns an Invoice.
                                        await subscriptionServices.updateSubscriptionStatus().then(() => {
                                            let skroClickID = cookies.get("sclid");
                                            if (skroClickID !== null && skroClickID !== "" && skroClickID !== undefined) {
                                                subscriptionServices.skroPostback({
                                                    clickId: skroClickID,
                                                    payout: totalPrice
                                                }).finally(() => {
                                                    document.location.replace("/");
                                                });
                                            } else {
                                                document.location.replace("/");
                                            }
                                        }).catch((error) => {
                                            setErrorMsg(error?.response?.data?.error);
                                        });
                                    }
                                }
                            });
                    } else if (result?.paymentIntentStatus === "requires_payment_method") {
                        setErrorMsg("Your card was declined. Please try a different payment method.");
                        setUpdateOnlyPaymentMethod(true);
                    }
                }).catch((error) => {
                    setErrorMsg(error?.response?.data?.error);
                    setUpdateOnlyPaymentMethod(true);
                }).finally(() => {
                    setLoading(false);
                });

        } else if (updateOnlyPaymentMethod) {
            // Update only payment method when creating a subscription in first place and it throws an error like
            // payment failed, then we need to call only retry invoice.
            await subscriptionServices.retryInvoice(paymentMethod?.id)
                .then(async (result) => {
                    if (result?.status === "active") {
                        document.location.replace("/");
                    } else if (result?.paymentIntentStatus === "requires_action" || result?.paymentIntentStatus === "requires_payment_method") {
                        await stripe.confirmCardPayment(result?.paymentIntentClientSecret, {payment_method: result?.paymentMethodId})
                            .then((result) => {
                                if (result?.error) {
                                    setErrorMsg(result?.error?.message);
                                } else {
                                    if (result.paymentIntent.status === 'succeeded') {
                                        // There's a risk of the customer closing the window before callback
                                        // execution. To handle this case, set up a webhook endpoint and
                                        // listen to invoice.paid. This webhook endpoint returns an Invoice.
                                        subscriptionServices.updateSubscriptionStatus().then(() => {
                                            document.location.replace("/");
                                        }).catch((error) => {
                                            setErrorMsg(error?.response?.data?.error);
                                        });
                                    }
                                }
                            });
                    }
                }).catch((error) => {
                    setErrorMsg(error?.response?.data?.error);
                }).finally(() => {
                    setLoading(false);
                });

        } else {
            setErrorMsg("Creating a subscription failed. Please contract our customer support to resolve that issue.");
            setLoading(false);
        }
    };

    const handleFreePlan = async () => {

        if (!acceptTerms) {
            setAcceptTermsError(true);
            return;
        }

        setLoading(true);
        setErrorMsg("");

        await subscriptionServices.create({
                planName, referralCode,
                name: billingDetails?.name,
                address1: billingDetails?.address1,
                address2: billingDetails?.address2,
                country: billingDetails?.country,
                city: billingDetails?.city,
                state: billingDetails?.state,
                zip: billingDetails?.zip,
                vatNumber: billingDetails?.vatNumber
            }
        ).then(async () => {
            let skroClickID = cookies.get("sclid");
            if (skroClickID !== null && skroClickID !== "" && skroClickID !== undefined) {
                await subscriptionServices.skroPostback(
                    {clickId: skroClickID, payout: 0}
                ).finally(() => {
                    document.location.replace("/");
                });
            } else {
                document.location.replace("/");
            }
        }).catch((error) => {
            setErrorMsg(error?.response?.data?.error);
        }).finally(() => {
            setLoading(false);
        });
    };

    const handleAcceptTerms = () => {
        setAcceptTermsError(false);
        if (acceptTerms) {
            setAcceptTerms(false);
        } else {
            setAcceptTerms(true);
        }
    };

    const handlePreviousStep = () => {
        setStep(2);
    };

    const handleApplyCoupon = async () => {
        setErrorMsg("");

        if (coupon === "") {
            setErrorMsg("Please enter a valid coupon code.")
            return false;
        }

        setLoading(true);
        await subscriptionServices.verifyCoupon(coupon).then((result) => {
            setCouponResult(result);
        }).catch((error) => {
            setErrorMsg(error?.response?.data?.error);
        }).finally(() => {
            setLoading(false);
        });
    };

    // Calculate amounts
    useEffect(() => {
        if (step === 3 && planName !== "" && planName !== undefined) {

            let price = 0.0;
            individualSubscriptionPlans.forEach((plan) => {
                if (planName === plan?.name) {
                    price = plan?.price;
                }
            });
            businessSubscriptionPlans.forEach((plan) => {
                if (planName === plan?.name) {
                    price = plan?.price;
                }
            });

            let vat = price * (vatRate / 100);
            let totalAmount = price + vat;

            setPlanPrice(price);
            setVatAmount(vat);
            setTotalPrice(totalAmount);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [planName, vatRate])

    // Apply coupon code discount in numbers.
    useEffect(() => {
        if (couponResult?.valid) {

            let price = 0.0;
            individualSubscriptionPlans.forEach((plan) => {
                if (planName === plan?.name) {
                    price = plan?.price;
                }
            });
            businessSubscriptionPlans.forEach((plan) => {
                if (planName === plan?.name) {
                    price = plan?.price;
                }
            });

            let vat = price * (vatRate / 100);
            let totalPrice = price + vat;

            let off = 0;
            if (couponResult?.percentOff !== 0) {
                off = totalPrice * (couponResult?.percentOff / 100);
            }
            if (couponResult?.amountOff !== 0) {
                if (couponResult?.amountOff > totalPrice) {
                    off = totalPrice;
                } else {
                    off = couponResult?.amountOff;
                }
            }
            setDiscount(off);
            setTotalPrice(totalPrice - off);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [couponResult])

    // Set referral code into cookies.
    useEffect(() => {
        const refer = cookies.get("refer");
        if (refer !== undefined) {
            setReferralCode(refer);
        }
    }, []);

    return (
        <AuthCard title={`Confirm your ${planName} subscription`} description="Review your plan, billing total, and payment details before continuing.">
            {loading && <Spinner size={32} overComponentBox={true}/>}

            <DialogContent>
                <Alert severity="info" variant="filled" sx={{mb: 2}}>
                    The payment amount will be charged automatically each month. Subscription starts after successful
                    payment.
                </Alert>
                <Box sx={{bgcolor: theme => getSettingsColors(theme).inset, p: 2, borderRadius: '10px'}}>
                <Box display="flex" flexDirection="row">
                    <Box flexGrow={1} p={1} sx={{fontWeight: 600}}>
                        Subscribing to plan
                    </Box>
                    <Box p={1}>{planName}</Box>
                </Box>
                <Box display="flex" flexDirection="row">
                    <Box flexGrow={1} p={1} sx={{fontWeight: 600}}>
                        Price
                    </Box>
                    <Box p={1}>€ {planPrice.toFixed(2)}</Box>
                </Box>
                <Box display="flex" flexDirection="row">
                    <Box flexGrow={1} p={1} sx={{fontWeight: 600}}>
                        {vatDisplayName}
                    </Box>
                    <Box p={1}>€ {vatAmount.toFixed(2)}</Box>
                </Box>
                {discount > 0 &&
                    <Box display="flex" flexDirection="row" sx={{color: "primary.main", fontWeight: 600}}>
                        <Box flexGrow={1} p={1} sx={{fontWeight: 600}}>
                            Discount
                        </Box>
                        <Box p={1}>- € {discount.toFixed(2)}</Box>
                    </Box>
                }
                <Divider/>
                <Box display="flex" flexDirection="row">
                    <Box flexGrow={1} p={1} sx={{fontWeight: 600}}>
                        Total due now
                    </Box>
                    <Box p={1}>€ {totalPrice.toFixed(2)}</Box>
                </Box>
                </Box>

                <Box display="flex" flexDirection="row" mt={3} mb={1}>
                    <Box flexGrow={1}>
                        <TextField
                            label="Referral code"
                            variant="outlined"
                            margin="dense"
                            name="referralCode"
                            type="text"
                            size="small"
                            onChange={(e) => setReferralCode(e.target.value)}
                            value={referralCode}
                            placeholder="Enter referral code"
                            fullWidth={true}
                            slotProps={{
                                inputLabel: {
                                    shrink: true
                                }
                            }}
                        />
                    </Box>
                </Box>

                {planName !== "Free" && <Box sx={{display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto',
                    alignItems: 'start', gap: 1, mt: 2, mb: 1}}>
                        <TextField
                            label="Coupon code"
                            variant="outlined"
                            margin="none"
                            name="coupon"
                            size="small"
                            type="text"
                            onChange={(e) => setCoupon(e.target.value)}
                            placeholder="Enter coupon code"
                            fullWidth={true}
                            slotProps={{
                                inputLabel: {
                                    shrink: true
                                }
                            }}
                        />
                        <Button
                            type="button"
                            color="primary"
                            variant="contained"
                            onClick={handleApplyCoupon}
                            disabled={loading}
                            sx={{height: 40, py: 0}}
                        >
                            Apply
                        </Button>
                </Box>
                }

                {couponResult?.valid &&
                    <CustomSnackbar message="Coupon code successfully applied!" timeout={2500} severity={"success"}/>
                }

                {errorMsg !== "" &&
                    <CustomSnackbar message={errorMsg} timeout={6500} severity={"error"}/>
                }

                {planName !== "Free" &&
                    <Box mt={4}>
                        <Box>
                            <form onSubmit={handleConfirmPayment}>
                                <Box sx={{fontWeight: 600, paddingLeft: 1, fontSize: 14}}>Card details</Box>
                                <AuthCardField/>
                                <AuthPaymentNote/>
                            </form>
                        </Box>
                    </Box>
                }
                <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 2, fontSize: 13, lineHeight: 1.7,
                    color: acceptTermsError ? 'error.main' : 'text.secondary'}}>
                    <Checkbox
                        checked={acceptTerms}
                        onChange={handleAcceptTerms}
                        name="terms"
                        slotProps={{input: {'aria-label': 'Accept Terms & Conditions'}}}
                        sx={{p: 0.5}}
                        color="primary"
                    />
                    <Box sx={{pt: 0.5}}>Read and accept our
                    <a
                        href="https://skro.eu/terms-and-conditions" target="_blank" rel="noreferrer"
                        style={{paddingLeft: "4px", color: theme.palette.primary.main}}
                    >
                        Terms & Conditions
                    </a></Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{padding: "12px 15px"}}>
                <AuthBackButton onClick={handlePreviousStep} disabled={loading}/>
                {planName === "Free" ?
                    <Button type="button" color="primary" variant="contained" onClick={handleFreePlan}
                            disabled={!acceptTerms || loading}>
                        Subscribe
                    </Button>
                    :
                    <Button type="submit" color="primary" variant="contained" onClick={handleConfirmPayment}
                            disabled={!acceptTerms || loading}>
                        Confirm Payment
                    </Button>
                }
            </DialogActions>
        </AuthCard>
    );
};
